import React, { useState, useEffect } from 'react';
import ConfirmModal from './ConfirmModal';
import { isoToRuDisplay } from '../utils/birthDateInput';
import './UsersManagement.css';

const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [moderators, setModerators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moderatorsLoading, setModeratorsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [moderatorsSearch, setModeratorsSearch] = useState('');
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  });

  const formatUserBirth = (v) => {
    if (v == null || v === '') return '—';
    if (typeof v === 'string') {
      const d = isoToRuDisplay(v);
      return d || '—';
    }
    if (v instanceof Date && !isNaN(v.getTime())) {
      const y = v.getFullYear();
      const m = String(v.getMonth() + 1).padStart(2, '0');
      const d = String(v.getDate()).padStart(2, '0');
      return `${d}.${m}.${y}`;
    }
    return '—';
  };

  useEffect(() => {
    fetchUsers();
    fetchModerators();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/auth/users', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Ошибка при загрузке пользователей');
      }

      const data = await response.json();
      setUsers(data);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchModerators = async () => {
    try {
      setModeratorsLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/auth/moderators-with-telegram', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Ошибка при загрузке модераторов');
      }

      const data = await response.json();
      setModerators(data);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setModeratorsLoading(false);
    }
  };

  const handleForceUnlink = (user) => {
    setConfirmModal({
      isOpen: true,
      title: 'Принудительная отвязка Telegram',
      message: `Вы уверены, что хотите отвязать Telegram для пользователя ${user.discord_username || user.email}? После этого пользователь не сможет войти, пока не привяжет Telegram заново.`,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`/api/auth/telegram/force-unlink/${user.id}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || 'Ошибка при отвязке Telegram');
          }

          setSuccess(data.message);
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
          fetchUsers();
          
          setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
          setError(err.message);
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
        }
      }
    });
  };

  const handleDelete = (user) => {
    setConfirmModal({
      isOpen: true,
      title: 'Удаление аккаунта',
      message: `Вы уверены, что хотите удалить аккаунт пользователя ${user.discord_username || user.email}? Это действие необратимо.`,
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`/api/auth/users/${user.id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || 'Ошибка при удалении пользователя');
          }

          setSuccess(data.message);
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
          fetchUsers();
          
          setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
          setError(err.message);
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
        }
      }
    });
  };

  // Фильтрация пользователей
  const filteredUsers = users.filter(user => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      user.email?.toLowerCase().includes(searchLower) ||
      user.discord_username?.toLowerCase().includes(searchLower) ||
      user.telegram_username?.toLowerCase().includes(searchLower) ||
      user.discord_user_id?.includes(search)
    );
  });

  // Фильтрация модераторов
  const filteredModerators = moderators.filter(moderator => {
    if (!moderatorsSearch) return true;
    const searchLower = moderatorsSearch.toLowerCase();
    return (
      moderator.nickname?.toLowerCase().includes(searchLower) ||
      moderator.discord_id?.includes(searchLower) ||
      moderator.telegram_username?.toLowerCase().includes(searchLower) ||
      moderator.server?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="users-management">
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
      />

      {error && (
        <div className="alert alert-error">
          {error}
          <button className="alert-close" onClick={() => setError('')}>×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
          <button className="alert-close" onClick={() => setSuccess('')}>×</button>
        </div>
      )}

      <div className="moderator-list-card">
        <div className="card-header">
          <h3>Двухфакторная авторизация на сайт ({filteredUsers.length})</h3>
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Поиск по email, Discord, Telegram..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="loading">Загрузка...</div>
        ) : filteredUsers.length === 0 ? (
          <div className="empty-state">
            {search ? 'Пользователи не найдены по запросу' : 'Пользователи не найдены'}
          </div>
        ) : (
          <div className="moderator-table-container">
            <table className="moderator-table">
              <thead>
                <tr>
                  <th>Discord</th>
                  <th>Email</th>
                  <th>День рождения</th>
                  <th>Telegram</th>
                  <th>Дата регистрации</th>
                  <th className="actions-column">Действия</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="user-discord-info">
                        <span className="discord-username">{user.discord_username || '—'}</span>
                        <span className="discord-id">{user.discord_user_id}</span>
                      </div>
                    </td>
                    <td>{user.email}</td>
                    <td>{formatUserBirth(user.birth_date)}</td>
                    <td>
                      {user.telegram_chat_id ? (
                        <div className="telegram-linked">
                          <span className="telegram-status-badge linked">
                            <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                              <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.912.489-1.302.481-.428-.008-1.252-.241-1.865-.44-.752-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635.099-.002.321.023.465.141.121.099.154.232.17.325.015.094.034.31.019.478z"/>
                            </svg>
                            {user.telegram_username ? `@${user.telegram_username}` : 'Привязан'}
                          </span>
                          <span className="telegram-linked-date">
                            {user.telegram_linked_at && new Date(user.telegram_linked_at).toLocaleDateString('ru-RU')}
                          </span>
                        </div>
                      ) : (
                        <span className="telegram-status-badge not-linked">Не привязан</span>
                      )}
                    </td>
                    <td>
                      {new Date(user.created_at).toLocaleString('ru-RU', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td className="actions-column">
                      {user.isProtected ? (
                        <span className="no-action" title="Недоступно для пользователей с ролью admin или Chief Manager">
                          Защищено
                        </span>
                      ) : (
                        <div className="action-buttons">
                          {user.telegram_chat_id && (
                            <button
                              className="btn-action btn-unlink"
                              onClick={() => handleForceUnlink(user)}
                              title="Отвязать Telegram"
                            >
                              Отвязать TG
                            </button>
                          )}
                          <button
                            className="btn-action btn-delete"
                            onClick={() => handleDelete(user)}
                            title="Удалить аккаунт"
                          >
                            Удалить
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="moderator-list-card">
        <div className="card-header">
          <h3>Уведомления Telegram ({filteredModerators.length})</h3>
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Поиск по никнейму, Discord, Telegram, серверу..."
              value={moderatorsSearch}
              onChange={(e) => setModeratorsSearch(e.target.value)}
            />
          </div>
        </div>

        {moderatorsLoading ? (
          <div className="loading">Загрузка...</div>
        ) : filteredModerators.length === 0 ? (
          <div className="empty-state">
            {moderatorsSearch ? 'Модераторы не найдены по запросу' : 'Модераторы с подключенными уведомлениями не найдены'}
          </div>
        ) : (
          <div className="moderator-table-container">
            <table className="moderator-table">
              <thead>
                <tr>
                  <th>Никнейм</th>
                  <th>Discord ID</th>
                  <th>Сервер</th>
                  <th>Telegram</th>
                  <th>Дата привязки</th>
                </tr>
              </thead>
              <tbody>
                {filteredModerators.map((moderator) => (
                  <tr key={moderator.id}>
                    <td>{moderator.nickname}</td>
                    <td>
                      <span className="discord-id">{moderator.discord_id}</span>
                    </td>
                    <td>{moderator.server || '—'}</td>
                    <td>
                      <div className="telegram-linked">
                        <span className="telegram-status-badge linked">
                          <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                            <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.912.489-1.302.481-.428-.008-1.252-.241-1.865-.44-.752-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635.099-.002.321.023.465.141.121.099.154.232.17.325.015.094.034.31.019.478z"/>
                          </svg>
                          {moderator.telegram_username ? `@${moderator.telegram_username}` : 'Привязан'}
                        </span>
                        <span className="telegram-linked-date">
                          {moderator.telegram_linked_at && new Date(moderator.telegram_linked_at).toLocaleDateString('ru-RU')}
                        </span>
                      </div>
                    </td>
                    <td>
                      {moderator.telegram_linked_at ? new Date(moderator.telegram_linked_at).toLocaleString('ru-RU', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsersManagement;

